// src/pages/AdminUsers.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Users, PERMISSIONS as ALL_PERMISSIONS } from "../utils/api";
import { useAuth } from "../auth/useAuth";
import { isAdmin } from "../auth/acl";

/** Tiện ích nhỏ để chuẩn hoá các kiểu response khác nhau */
function asArray(x) {
    const d = x && x.data ? x.data : x;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d?.rows)) return d.rows;
    return [];
}

export default function AdminUsers() {
    // ---- ACL ----
    const { perms, loading: authLoading } = useAuth();
    const amAdmin = isAdmin(perms);

    // Chặn non-admin
    useEffect(() => {
        if (!authLoading && !amAdmin) {
            toast.error("Bạn không có quyền truy cập");
            window.location.replace("/"); // hoặc điều hướng về trang chủ
        }
    }, [authLoading, amAdmin]);

    // ---- State ----
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    // form
    const [id, setId] = useState(null); // null = create
    const [username, setUsername] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState(""); // optional
    const [password, setPassword] = useState("");
    const [active, setActive] = useState(1);
    const [permsForm, setPermsForm] = useState([]);

    // dropdown quyền
    const [openPerms, setOpenPerms] = useState(false);
    const permsRef = useRef(null);

    useEffect(() => {
        function onClickOutside(e) {
            if (permsRef.current && !permsRef.current.contains(e.target)) {
                setOpenPerms(false);
            }
        }
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    // ---- CRUD ----
    function resetForm() {
        setId(null);
        setUsername("");
        setDisplayName("");
        setEmail("");
        setPassword("");
        setActive(1);
        setPermsForm([]);
    }

    const loadUsers = useCallback(async () => {
        if (!amAdmin) return; // tránh gọi khi non-admin
        try {
            setLoading(true);
            const list = await Users.list();
            const data = asArray(list);
            // Ẩn user 'admin' với non-admin (phòng hờ, dù trang này đã chặn)
            const dataSafe = amAdmin ? data : data.filter(u => u.username !== "admin");
            setRows(dataSafe);
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.message || "Không tải được danh sách user");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [amAdmin]);
    useEffect(() => { if (amAdmin) loadUsers(); }, [amAdmin, loadUsers]);

    // validate
    const creating = !id;
    const usernameOk = username.trim().length >= 3;
    const passwordOk = creating ? password.trim().length >= 6 : true;
    const canSave = creating ? (usernameOk && passwordOk) : (usernameOk && !!id);

    async function save() {
        try {
            if (!usernameOk) return toast.error("Tài khoản tối thiểu 3 ký tự");
            if (creating && !passwordOk) return toast.error("Mật khẩu tối thiểu 6 ký tự");

            const safeEmail = (email || "").trim(); // backend sẽ xử lý fallback nếu cần

            if (id) {
                // UPDATE
                await Users.update(id, {
                    username,
                    display_name: displayName,
                    email: safeEmail || undefined,
                    is_active: active,
                    password: password || undefined, // nhập mới thì đổi pass
                });

                // Set quyền (ghi đè toàn bộ)
                await Users.setPermissions(id, permsForm);

                toast.success("Đã cập nhật user");
            } else {
                // CREATE
                const created = await Users.create({
                    username,
                    password,
                    display_name: displayName,
                    email: safeEmail,
                    is_active: active,
                    permissions: permsForm,
                });

                const newId =
                    created?.user?.id ?? created?.id ?? created?.item?.id ?? null;

                if (newId && permsForm?.length) {
                    await Users.setPermissions(newId, permsForm).catch(() => { });
                }

                toast.success("Đã tạo user");
            }

            await loadUsers();
            resetForm();
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Lưu không thành công";
            toast.error(msg);
        }
    }

    function pick(u) {
        setId(u.id);
        setUsername(u.username ?? u.email ?? "");
        setDisplayName(u.display_name ?? u.displayName ?? "");
        setEmail(u.email ?? "");
        setPassword("");
        setActive((u.is_active ?? u.isActive) ? 1 : 0);
        setPermsForm(u.permissions || []);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const togglePerm = (p) =>
        setPermsForm((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

    async function removeUser() {
        if (!id) return;
        if (!window.confirm("Xoá user này?")) return;
        try {
            await Users.del(id);
            toast.success("Đã xoá user");
            await loadUsers();
            resetForm();
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.message || e?.message || "Xoá không thành công";
            toast.error(msg);
        }
    }

    // render helpers
    const selectedLabels = (ALL_PERMISSIONS || [])
        .filter((p) => permsForm.includes(p.key))
        .map((p) => p.label);

    const list = Array.isArray(rows) ? rows : [];

    // Nếu đang kiểm tra quyền: không render gì tránh lỗi hook
    if (authLoading) return null;
    if (!amAdmin) return null; // đã redirect ở useEffect rồi

    return (
        <div className="p-3 sm:p-4">
            <h2 className="text-base sm:text-lg font-semibold mb-3">Quản lý user</h2>

            {/* Form */}
            <div className="bg-white/60 rounded-xl p-3 shadow mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                        <label className="block text-sm mb-1">
                            Tài khoản <span className="text-red-600">*</span>
                        </label>
                        <input
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 ${username && !usernameOk ? "border-red-500" : ""
                                }`}
                            placeholder="username"
                        />
                        {!usernameOk && username && (
                            <div className="text-xs text-red-600 mt-1">Tối thiểu 3 ký tự</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm mb-1">Tên hiển thị</label>
                        <input
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2"
                            placeholder="Nguyễn Văn A"
                        />
                    </div>

                    <div>
                        <label className="block text-sm mb-1">Email (không bắt buộc)</label>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2"
                            placeholder="user@example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm mb-1">
                            {id ? "Đổi mật khẩu (tuỳ chọn)" : <>Mật khẩu <span className="text-red-600">*</span></>}
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 ${creating && password && !passwordOk ? "border-red-500" : ""
                                }`}
                            placeholder="Tối thiểu 6 ký tự"
                        />
                        {creating && password && !passwordOk && (
                            <div className="text-xs text-red-600 mt-1">
                                Mật khẩu tối thiểu 6 ký tự
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            id="active"
                            type="checkbox"
                            checked={!!active}
                            onChange={(e) => setActive(e.target.checked ? 1 : 0)}
                        />
                        <label htmlFor="active">Kích hoạt</label>
                    </div>
                </div>

                {/* Dropdown Quyền */}
                <div className="mt-3" ref={permsRef}>
                    <div className="text-sm mb-1">Quyền</div>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setOpenPerms((v) => !v)}
                            className="w-full sm:w-auto px-3 py-2 rounded-lg border bg-white flex items-center gap-2"
                        >
                            <span className="truncate">
                                {selectedLabels.length
                                    ? selectedLabels.join(", ")
                                    : "Chọn quyền"}
                            </span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 transition ${openPerms ? "rotate-180" : ""}`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>

                        {openPerms && (
                            <div className="absolute z-20 mt-1 w-64 bg-white border rounded-xl shadow p-2">
                                <div className="grid grid-cols-1 gap-2">
                                    {(ALL_PERMISSIONS || []).map((p) => (
                                        <label key={p.key} className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={permsForm.includes(p.key)}
                                                onChange={() => togglePerm(p.key)}
                                            />
                                            <span>{p.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        onClick={save}
                        disabled={!canSave}
                        className={`px-4 py-2 rounded-lg text-white ${canSave ? "bg-blue-600" : "bg-blue-300 cursor-not-allowed"
                            }`}
                    >
                        {id ? "Lưu" : "Tạo user"}
                    </button>
                    <button onClick={resetForm} className="px-4 py-2 rounded-lg border">
                        Mới
                    </button>
                    {id && (
                        <button
                            onClick={removeUser}
                            className="px-4 py-2 rounded-lg bg-red-600 text-white"
                        >
                            Xoá
                        </button>
                    )}
                </div>
            </div>

            {/* Danh sách */}
            <div className="bg-white/60 rounded-xl shadow overflow-hidden">
                <div className="hidden sm:grid grid-cols-12 px-3 py-2 text-xs font-medium bg-gray-50">
                    <div className="col-span-3">Tài khoản</div>
                    <div className="col-span-3">Tên</div>
                    <div className="col-span-2">Trạng thái</div>
                    <div className="col-span-4">Quyền</div>
                </div>
                {loading ? (
                    <div className="p-3">Đang tải…</div>
                ) : list.length === 0 ? (
                    <div className="p-3">Chưa có user.</div>
                ) : (
                    <ul className="divide-y">
                        {list.map((u) => (
                            <li
                                key={u.id}
                                onClick={() => pick(u)}
                                className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                                {/* mobile */}
                                <div className="sm:hidden text-sm">
                                    <div className="flex justify-between">
                                        <span className="font-medium">{u.username ?? u.email}</span>
                                        <span
                                            className={
                                                (u.is_active ?? u.isActive) ? "text-green-600" : "text-gray-400"
                                            }
                                        >
                                            {(u.is_active ?? u.isActive) ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div className="truncate text-gray-600">
                                        {(u.permissions || []).join(", ")}
                                    </div>
                                </div>
                                {/* desktop */}
                                <div className="hidden sm:grid grid-cols-12">
                                    <div className="col-span-3 truncate">{u.username ?? u.email}</div>
                                    <div className="col-span-3 truncate">
                                        {u.display_name ?? u.displayName ?? ""}
                                    </div>
                                    <div className="col-span-2">
                                        {(u.is_active ?? u.isActive) ? "Active" : "Inactive"}
                                    </div>
                                    <div className="col-span-4 truncate">
                                        {(u.permissions || []).join(", ")}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
